import { useMutation, useApolloClient } from "@apollo/client";
import { UNIFIED_CHECKOUT } from "@shop/graphql/mutations/unifiedCheckout";
import { useEffect } from "react";
import { clearSelectedProducts, setCheckoutStatus, useShopSlice, setSelectedProducts } from "@store/slices/shop";
import { useDispatch } from "react-redux";
import { useCheckoutPersistence } from "./useCheckoutPersistence";
import { handleErrorWithNotification, mapGraphQLErrorToFriendlyMessage } from "@/utils/errorHandling";
import { FETCH_SUBSCRIPTION_INFO } from "@company/graphql/queries/fetchSubscriptionInfo";
import { validateAuthenticationBeforeCheckout } from "@/utils/authValidation";

export const useUnifiedCheckout = () => {
    const dispatch = useDispatch();
    const client = useApolloClient();
    const [unifiedCheckoutMutation, { data: checkoutData }] = useMutation(UNIFIED_CHECKOUT);
    const { selectedProducts } = useShopSlice();
    const { clearCheckoutState } = useCheckoutPersistence();

    function generateAttemptId(): string {
        // short, URL-safe nonce: 8 chars base36 timestamp+random
        const rnd = Math.floor(Math.random() * 36 ** 4).toString(36).padStart(4, '0');
        const ts = (Date.now() % 36 ** 4).toString(36).padStart(4, '0');
        return `${ts}${rnd}`;
    }

    useEffect(() => {
        if (checkoutData?.unifiedCheckout) {
            const result = checkoutData.unifiedCheckout;

            // Try to parse JSON response to check for special cases
            let parsedResult = null;
            try {
                parsedResult = JSON.parse(result);
            } catch (e) {
                // Not JSON, continue with normal string processing
            }

            // Handle error responses (all products invalid)
            if (parsedResult?.error === 'INVALID_PRODUCTS_REMOVED') {
                console.warn("❌ Alle Produkte ungültig:", parsedResult);
                dispatch(setCheckoutStatus("error"));
                
                // Clear all selected products since they're all invalid
                dispatch(clearSelectedProducts());
                
                handleErrorWithNotification(
                    new Error('invalid_products_all_removed'),
                    dispatch,
                    "Produkte nicht verfügbar",
                    parsedResult.message
                );
                return;
            }

            // Handle success responses with removed products
            if (parsedResult?.removedProducts && parsedResult.removedProducts.length > 0) {
                console.warn("⚠️ Einige Produkte entfernt:", parsedResult);
                
                // Remove invalid products from cart
                const invalidPriceIds = parsedResult.removedProducts;
                const validProducts = selectedProducts.filter(
                    product => !invalidPriceIds.includes(product.priceId)
                );
                
                // Update cart state with only valid products
                dispatch(setSelectedProducts(validProducts));
                
                // Show warning notification about removed products
                handleErrorWithNotification(
                    new Error('products_removed_from_cart'),
                    dispatch,
                    "Warenkorb aktualisiert",
                    parsedResult.message
                );

                // Continue processing the actual result
                const actualResult = parsedResult.result;
                
                if (actualResult === "SEAT_UPDATES_COMPLETED") {
                    console.log("✅ Sitz-Updates erfolgreich abgeschlossen (mit entfernten Produkten)");
                    dispatch(setCheckoutStatus("success"));
                    client.refetchQueries({ include: [FETCH_SUBSCRIPTION_INFO] }).catch(() => {/* best-effort */});
                    clearCheckoutState();
                } else if (actualResult.startsWith("http")) {
                    console.log("🔗 Weiterleitung zu Stripe Checkout (mit entfernten Produkten):", actualResult);
                    window.location.href = actualResult;
                }
                return;
            }

            // Normal response processing (no products removed)
            if (result === "SEAT_UPDATES_COMPLETED") {
                console.log("✅ Sitz-Updates erfolgreich abgeschlossen");
                dispatch(setCheckoutStatus("success"));

                // Seats updated server-side → refresh subscription info so UI shows new counts
                client.refetchQueries({ include: [FETCH_SUBSCRIPTION_INFO] }).catch(() => {/* best-effort */});

                dispatch(clearSelectedProducts());

                // Clear any persisted checkout state since checkout is finished
                clearCheckoutState();

                // Optional: Seite neu laden oder Abonnement neu abrufen
                // window.location.reload();
            } else if (result.startsWith("http")) {
                // Es ist eine Checkout-URL, weiterleiten zu Stripe
                console.log("🔗 Weiterleitung zu Stripe Checkout:", result);
                window.location.href = result;
            } else {
                console.error("Unerwartete Checkout-Antwort:", result);
                dispatch(setCheckoutStatus("error"));
            }
        }
    }, [checkoutData, dispatch, client, clearCheckoutState]);

    return async () => {
        console.log("🚀 EINHEITLICHER CHECKOUT GESTARTET");
        console.log("📦 Ausgewählte Produkte:", selectedProducts);

        // Validieren dass Produkte ausgewählt sind
        if (selectedProducts.length === 0) {
            console.warn("❌ Keine Produkte für Checkout ausgewählt");
            return;
        }

        dispatch(setCheckoutStatus("loading"));

        // Pre-flight authentication check
        const authCheck = await validateAuthenticationBeforeCheckout(client);
        if (!authCheck.isValid) {
            console.warn('🚨 Pre-checkout authentication check failed:', authCheck.error);
            dispatch(setCheckoutStatus("error"));
            
            let errorMessage = "Authentication required to continue with checkout.";
            if (authCheck.error === 'authentication_expired') {
                errorMessage = "Your session has expired. Please log in again to continue.";
            } else if (authCheck.error === 'no_user_profile') {
                errorMessage = "Unable to verify your account. Please log in again.";
            }
            
            handleErrorWithNotification(
                new Error(`auth_check_failed_${authCheck.error}`), 
                dispatch, 
                "Session Expired", 
                errorMessage
            );
            
            // Let the existing App.tsx route protection handle redirect
            return;
        }

        try {
            const attemptId = generateAttemptId();
            const result = await unifiedCheckoutMutation({
                variables: {
                    products: selectedProducts.map((product) => ({
                        priceId: product.priceId,
                        quantity: product.quantity,
                    })),
                    attemptId,
                },
            });

            console.log("✅ Einheitlicher Checkout abgeschlossen:", result);
        } catch (error: any) {
            console.error("❌ Einheitlicher Checkout fehlgeschlagen:", error);
            dispatch(setCheckoutStatus("error"));
            handleErrorWithNotification(error as any, dispatch, "Checkout fehlgeschlagen", mapGraphQLErrorToFriendlyMessage(error as any));
            // do not rethrow to prevent unhandled promise rejections in UI handlers
        }
    };
};
